<template>
  <v-flex
    class="primary"
    column
  >
    <v-parallax
      src="https://gm.goz.fun/wp-content/uploads/2023/03/about-cover.jpg"
      height="400"
      role="img"
      aria-label="Artwork banner for the About page"
    />

    <v-container>
      <v-card>
        <v-app-bar
          flat
          color="primary"
          class="font-weight-thin text-uppercase"
          role="heading"
          aria-level="2"
        >
          {{ $t('components.about.whoWeAre.title') }}
        </v-app-bar>

        <v-card-text>
          {{ $t('components.about.whoWeAre.description') }}
        </v-card-text>

        <v-app-bar
          flat
          color="primary"
          class="font-weight-thin text-uppercase"
          role="heading"
          aria-level="2"
        >
          {{ $t('components.about.whatWeDo.title') }}
        </v-app-bar>

        <v-card-text>
          {{ $t('components.about.whatWeDo.description') }}
        </v-card-text>

        <v-app-bar
          flat
          color="primary"
          class="font-weight-thin text-uppercase"
          role="heading"
          aria-level="2"
        >
          {{ $t('components.about.founder.title') }}
        </v-app-bar>

        <v-card-text>
          {{ $t('components.about.founder.description.0') }}
          <v-btn
            elevation="0"
            x-small
            color="transparent"
            target="_blank"
            :href="communities.founderGoku"
            aria-label="Open founder profile in new tab"
            role="link"
          >
            {{ $t('components.about.founder.boldStatement') }}
          </v-btn>
          {{ $t('components.about.founder.description.1') }}
        </v-card-text>

        <v-app-bar
          flat
          color="primary"
          class="font-weight-thin text-uppercase"
          role="heading"
          aria-level="2"
        >
          {{ $t('components.about.projects.amw.title') }}
        </v-app-bar>

        <v-card-text>
          {{ $t('components.about.projects.amw.description.0') }}
          <v-btn
            elevation="0"
            x-small
            color="transparent"
            target="_blank"
            :href="communities.amwPage"
            aria-label="Visit AMW website (opens new tab)"
            role="link"
          >
            {{ $t('components.about.projects.amw.boldStatement') }}
          </v-btn>
          {{ $t('components.about.projects.amw.description.1') }}
        </v-card-text>

        <v-app-bar
          flat
          color="primary"
          class="font-weight-thin text-uppercase"
          role="heading"
          aria-level="2"
        >
          {{ $t('components.about.projects.amw.foundedTitle') }}
        </v-app-bar>

        <v-card-text>
          {{ $t('components.about.projects.amw.description.0') }}
          <v-btn
            elevation="0"
            x-small
            color="transparent"
            target="_blank"
            :href="communities.amwCompanyPage"
            aria-label="Open AMW company profile (new tab)"
            role="link"
          >
            {{ $t('components.about.projects.amw.foundedBoldStatement') }}
          </v-btn>
          {{ $t('components.about.projects.amw.description.1') }}
        </v-card-text>

        <v-app-bar
          flat
          color="primary"
          class="font-weight-thin text-uppercase"
          role="heading"
          aria-level="2"
        >
          {{ $t('components.about.founder.countryTitle') }}
        </v-app-bar>

        <v-card-text>
          {{ $t('components.about.founder.countryName') }}
        </v-card-text>

        <v-app-bar
          flat
          color="primary"
          class="font-weight-thin text-uppercase"
          role="heading"
          aria-level="2"
        >
          {{ $t('components.about.projects.gozgame.title') }}
        </v-app-bar>

        <v-card-text>
          {{ $t('components.about.projects.gozgame.description.0') }}
          <v-btn
            elevation="0"
            x-small
            color="transparent"
            target="_blank"
            :href="goodsStore"
            aria-label="Open Gods of Zushin Store (new tab)"
            role="link"
          >
            {{ $t('components.about.projects.gozgame.boldStatement') }}
          </v-btn>
          {{ $t('components.about.projects.gozgame.description.1') }}
        </v-card-text>

        <v-app-bar
          flat
          color="primary"
          class="font-weight-thin text-uppercase"
          role="heading"
          aria-level="2"
        >
          <v-toolbar-title>
            {{ $t('components.about.projects.goz.title') }}
          </v-toolbar-title>
        </v-app-bar>
      </v-card>

      <v-divider />

      <!-- Loading bar is a polite live region -->
      <v-progress-linear
        v-show="isLoading"
        color="accent"
        indeterminate
        role="status"
        aria-label="Loading timeline"
      />

      <!-- Timeline list -->
      <v-timeline
        v-show="!isLoading"
        aria-label="Company achievements timeline"
        role="list"
      >
        <v-timeline-item
          v-for="(achievement, i) in timeline"
          :key="i"
          :color="achievement.color"
          small
          role="listitem"
        >
          <template #opposite>
            <span
              :class="`headline font-weight-bold ${achievement.color}--text`"
              v-text="achievement.date"
            />
          </template>

          <div class="py-4">
            <h3
              :class="`headline font-weight-light mb-4 ${achievement.titleColor}--text lighten-1`"
            >
              {{ achievement.title }}
            </h3>
            <p>{{ achievement.description }}</p>
          </div>
        </v-timeline-item>
      </v-timeline>

      <v-divider />

      <!-- ────────────────────────── JOIN US ──────────────────────────────── -->
      <v-card>
        <v-app-bar
          flat
          color="primary"
          class="font-weight-thin text-uppercase"
          role="heading"
          aria-level="2"
        >
          {{ $t('components.about.projects.join.title') }}
        </v-app-bar>

        <v-card-text>
          <span class="font-weight-thin">
            {{ $t('components.about.projects.join.description', { email }) }}
          </span>
        </v-card-text>
      </v-card>

      <!-- ────────────────────────── OTHER PROJECTS ─────────────────────────── -->
      <v-card>
        <v-app-bar
          flat
          color="primary"
          class="font-weight-thin text-uppercase"
          role="heading"
          aria-level="2"
        >
          <v-toolbar-title class="font-weight-thin text-body-2">
            {{ $t('components.about.projects.others.title') }}
          </v-toolbar-title>

          <v-progress-linear
            v-show="isLoading"
            color="accent"
            indeterminate
            role="status"
            aria-label="Loading project list"
          />
        </v-app-bar>
      </v-card>

      <v-layout wrap>
        <v-flex
          v-for="project in projects"
          :key="project.id"
          full-width
          v-bind="{ [`xs${project.flexExtraSmall} sm${project.flexSmall} md${project.flexMedium} lg${project.flexLarge}`]: true }"
        >
          <!-- Active card only -->
          <v-card
            v-if="project.isActive"
            color="secondary"
            full-width
            :aria-labelledby="`project-title-${project.id}`"
          >
            <v-img
              :src="project.img"
              :alt="project.title"
              height="100px"
            />

            <v-card-title primary-title>
              <div>
                <h4
                  :id="`project-title-${project.id}`"
                  class="h5"
                >
                  {{ project.title }}
                </h4>
                <span class="grey--text text-subtitle-2">{{ project.subtitle }}</span>
              </div>
            </v-card-title>

            <v-card-actions>
              <v-btn
                text
                color="accent"
                :aria-label="'Open project {{ project.title }} in a new tab'"
                role="link"
                @click="viewProjectCard(project.link)"
              >
                {{ $t('common.view') || 'View' }}
              </v-btn>

              <v-spacer />

              <v-btn
                icon
                :color="isCardVisible(project.id) ? 'accent' : ''"
                role="button"
                :aria-label="isCardVisible(project.id)
                  ? `Collapse details of ${project.title}`
                  : `Expand details of ${project.title}`"
                @click="toggleProjectCard(project.id)"
              >
                <v-icon>
                  {{ isCardVisible(project.id) ? 'keyboard_arrow_down' : 'keyboard_arrow_up' }}
                </v-icon>
              </v-btn>
            </v-card-actions>

            <v-slide-y-transition>
              <v-card-text v-show="isCardVisible(project.id)">
                {{ project.brief }}
              </v-card-text>
            </v-slide-y-transition>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>
  </v-flex>
</template>

<script>
import projects from "@/constants/projects.js";
import company from "@/constants/company.timeline";
import config from "@/constants/config";
import links from "@/constants/links";

export default {
  name: "AboutUs",
  data() {
    return {
        communities: links.projects,
        people: links.people,
        cardsOptions: [],
        isLoading: false,
        email: config.email,
        goodsStore: links.goodsStore
    }
  },
  computed: {
    timeline() {
        return company.list.map((item) => ({
            ...item,
            title: this.$t(item.title),
            description: this.$t(item.description)
        }))
    },
    projects() {
        return projects.list.map((item) => ({
            ...item,
            title: this.$t(item.title),
            subtitle: this.$t(item.subtitle),
            brief: this.$t(item.brief)
        }))
    },
  },
  beforeMount() {
    this.isLoading = true;
    // Setup our state
    this.projects.map(({ id }) => {
      this.cardsOptions[id] = {
        visibile: false
      };
    });
  },
  mounted() {
    this.isLoading = false;
  },
  methods: {
    /**
     * Will handle the toggle of showing the text for the current card
     */
    toggleProjectCard(id) {
      this.cardsOptions.splice(id, 1, {
        ...this.cardsOptions[id],
        visibile: !this.cardsOptions[id].visibile
      });
    },
    /**
     * Will handle the view project card to external url
     */
    viewProjectCard(link) {
      if (link) {
        // track event
        // this.$ga.event("project-list", link, "");
        // open link
        window.open(link);
      }
    },
    /**
     * Check if card visible or not
     */
    isCardVisible(id) {
      return this.cardsOptions[id].visibile;
    }
  }
};
</script>
